import { OilData, VibrationTable } from 'queries/externalReferences/contract';

export type UpdateOilAnalysisParams = {
  descriptions: string[];
  actualData: OilData[];
  value: boolean;
  fieldToUpdate: string;
};

export type SelectedCell = {
  description: string;
  columnKey: string;
  value: string | null;
};

export type ExtendedOilData = OilData & { [key: string]: string | boolean | null };

export type ExtendedVibrationTable = VibrationTable & {
  [key: string]: string | boolean | null | { value: boolean };
};

export type HandleAnalysisChange = {
  description: string;
  columnKey: string;
  value: boolean;
};

export const getDescriptionFromRow = (row: ExtendedOilData): string => {
  const descriptionKey = Object.keys(row).find((key) => /[a-zA-Z]/.test(key));
  return descriptionKey ? (row[descriptionKey] as string) || '' : '';
};

export const getDescriptionFromVibrationRow = (row: ExtendedVibrationTable): string => {
  return row.id || '';
};

export const getCellSelectionKey = (columnKey: string): string => {
  return `selected_${columnKey}`;
};

export const isCellSelected = (
  row: ExtendedOilData | ExtendedVibrationTable,
  columnKey: string,
): boolean => {
  return Boolean(row[getCellSelectionKey(columnKey)]);
};

export type ExtendedDataRow = ExtendedOilData | ExtendedVibrationTable;

export const hasAnySelectedCells = (data: ExtendedDataRow[]): boolean => {
  return data.some((row) => {
    return Object.keys(row).some((key) => {
      return key.startsWith('selected_') && Boolean(row[key]);
    });
  });
};

export const getSelectedCells = (
  data: ExtendedOilData[] | ExtendedVibrationTable[],
  getDescription: (row: ExtendedOilData | ExtendedVibrationTable) => string,
  getValue: (row: ExtendedOilData | ExtendedVibrationTable, columnKey: string) => string | null,
): SelectedCell[] => {
  const selectedCells: SelectedCell[] = [];

  data.forEach((row) => {
    const description = getDescription(row);

    Object.keys(row).forEach((key) => {
      if (key.startsWith('selected_') && Boolean(row[key])) {
        const columnKey = key.replace('selected_', '');
        const value = getValue(row, columnKey);

        selectedCells.push({
          description,
          columnKey,
          value,
        });
      }
    });
  });

  return selectedCells;
};

export const getSelectedOilCells = (data: ExtendedOilData[]): SelectedCell[] => {
  return getSelectedCells(
    data,
    (row) => getDescriptionFromRow(row as ExtendedOilData),
    (row, columnKey) => (row as ExtendedOilData)[columnKey] as string | null,
  );
};

export const getSelectedVibrationCells = (data: ExtendedVibrationTable[]): SelectedCell[] => {
  return getSelectedCells(
    data,
    (row) => getDescriptionFromVibrationRow(row as ExtendedVibrationTable),
    (row, columnKey) => {
      const vibrationData = (row as ExtendedVibrationTable)[columnKey];

      if (
        vibrationData &&
        typeof vibrationData === 'object' &&
        vibrationData !== null &&
        'status' in vibrationData &&
        vibrationData.status !== undefined
      ) {
        return vibrationData.status as string;
      }

      return null;
    },
  );
};

export const handleAnalysisSave = (
  selectedCells: SelectedCell[],
  assetId: string,
  mutateFn: (data: { assetId: string; selectedCells: SelectedCell[] }) => void,
) => {
  if (selectedCells.length > 0) {
    mutateFn({
      assetId,
      selectedCells,
    });
  }
};

export const handleOilAnalysisSave = handleAnalysisSave;
export const handleVibrationAnalysisSave = handleAnalysisSave;

export const updateCellSelection = <T extends ExtendedDataRow>({
  data,
  description,
  columnKey,
  value,
  getDescription,
}: {
  data: T[];
  description: string;
  columnKey: string;
  value: boolean;
  getDescription: (row: T) => string;
}): T[] => {
  return data.map((item) => {
    const itemDescription = getDescription(item);

    if (itemDescription === description) {
      return {
        ...item,
        [getCellSelectionKey(columnKey)]: value,
      } as T;
    }

    return item;
  });
};

export const updateOilCellSelection = ({
  data,
  description,
  columnKey,
  value,
}: {
  data: ExtendedOilData[];
  description: string;
  columnKey: string;
  value: boolean;
}): ExtendedOilData[] => {
  return updateCellSelection({
    data,
    description,
    columnKey,
    value,
    getDescription: getDescriptionFromRow,
  });
};

export const updateVibrationCellSelection = ({
  data,
  description,
  columnKey,
  value,
}: {
  data: ExtendedVibrationTable[];
  description: string;
  columnKey: string;
  value: boolean;
}): ExtendedVibrationTable[] => {
  return updateCellSelection({
    data,
    description,
    columnKey,
    value,
    getDescription: getDescriptionFromVibrationRow,
  });
};

export const updateOilAnalysisData = ({
  actualData,
  descriptions,
  value,
  fieldToUpdate,
}: UpdateOilAnalysisParams): ExtendedOilData[] => {
  const updatedData = actualData.map((oilItem) => {
    const descriptionKey = Object.keys(oilItem).find((key) => /[a-zA-Z]/.test(key));
    const description = descriptionKey ? oilItem[descriptionKey] : '';

    if (!description || !descriptions.includes(description)) {
      return oilItem as ExtendedOilData;
    }

    return {
      ...oilItem,
      [fieldToUpdate]: value,
    } as ExtendedOilData;
  });

  return updatedData;
};
